import _ from "lodash";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { mergeClass, pause, removeTags } from "../../utils";
import { useEffect, useState } from "react";
import Select from "react-select";
import { isVideo } from "../Upload";
import { getStillVideoFrameFromUrl, uploadFile } from "./FileHandling/Utils";
import { tMsg } from "../ToastNotifications";
import { Spinner } from "../Loader";
import { useAtomValue } from "jotai";
import { hostDataAtom, useModifyUploadList } from "../../atoms";
import { IDropdownOption, dropdownFields } from "../../utils/customisation";
import { Button } from "../Button";
import {
  StringArrayItem,
  TextInputForStringArray,
} from "./stringArrayComponents";
import { format } from "date-fns";

interface IBaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: any;
  name: string;
  type: keyof typeof FieldList | string;
  modelname?: string;
  onChange: (value: any) => void;
}

interface IDropdownProps extends IBaseInputProps {
  options?: IDropdownOption[];
}

const fieldId = (name: string): string => `form_${name}`;

const FieldName = ({
  name,
  hint,
  required,
}: {
  name: string;
  hint?: string | React.ReactNode;
  required?: boolean;
}): JSX.Element => (
  <div>
    <div className="font-medium">
      {name}
      {required && <span className="text-red-500"> *</span>}
    </div>
    {hint && <div className="text-xs opacity-60">{hint}</div>}
  </div>
);

const TextOrNumber = ({
  name,
  value,
  ...restProps
}: IBaseInputProps): JSX.Element => (
  <input
    id={fieldId(name)}
    value={value || ""}
    className="w-full border border-[#ccc] p-2.5 text-sm"
    {...restProps}
  />
);

const StringArray = (props: IBaseInputProps): JSX.Element => {
  const { name, onChange, value } = props;
  const [hasCheckedQuotes, setHasCheckedQuotes] = useState<boolean>(false);

  useEffect(() => {
    removeQuotes();
  }, []);

  const removeQuotes = async () => {
    try {
      if (value && _.isArray(value)) {
        const checkForQuotes = value.map((v) => v.replaceAll('"', ""));
        onChange({ target: { value: checkForQuotes } });
        await pause(100);
      }
    } finally {
      setHasCheckedQuotes(true);
    }
  };

  const curValue = (_.isArray(value) && value) || [];

  const addValueToList = (val: string) => {
    const valueToAdd = [...curValue, val];
    onChange({ target: { value: valueToAdd } });
  };

  const removeValueFromList = (indexToRemove: number) => {
    const newList = curValue.filter((a, index) => index !== indexToRemove);
    onChange({ target: { value: newList } });
  };

  const removeAllDonors = () => {
    onChange({ target: { value: [] } });
  };

  return (
    <div id={fieldId(name)} className="space-y-4 pb-4">
      <TextInputForStringArray addValueFunction={addValueToList} />
      <div className="grid grid-cols-3 gap-3">
        {hasCheckedQuotes &&
          curValue.map((donor, index) => (
            <StringArrayItem
              name={donor}
              key={index}
              onClick={(e) => {
                e.preventDefault();
                removeValueFromList(index);
              }}
            />
          ))}
      </div>
      <div className="text-sm text-grayText my-2 font-medium">
        {curValue.length > 0 ? (
          <>
            <div>Total items: {curValue.length}</div>
            <button
              onClick={(e) => {
                e.preventDefault();
                removeAllDonors();
              }}
              className="text-sm underline text-darkGrayBg hover:text-grayText"
            >
              Remove All
            </button>
          </>
        ) : (
          "No items - use the input above to add a donor"
        )}
      </div>
    </div>
  );
};

const Checkbox = (props: IBaseInputProps): JSX.Element => {
  const { name, value, onChange } = props;
  const checkboxVal = _.isBoolean(value) && value === true;
  return (
    <input
      type="checkbox"
      className="h-4 w-3.5"
      id={fieldId(name)}
      checked={checkboxVal}
      onChange={(e) => {
        onChange({ target: { value: e.target.checked } });
      }}
    />
  );
};

const RichTextInput = (props: IBaseInputProps): JSX.Element => {
  const countWords = (text: any) => {
    if (_.isString(text)) {
      const cleanHtml = text.replaceAll(/<[^>]+>/g, "");
      return _.words(cleanHtml).length;
    } else if (_.isNumber(text)) return _.words(text.toString()).length;
    else return 0;
  };
  const { name, value, onChange } = props;
  const [wordCount, setWordCount] = useState<number>(countWords(value));

  const modules = {
    toolbar: [["italic"], ["clean"]],
  };
  const getValue = _.isString(value) ? value : "";

  // Attribute fonts
  const Font = ReactQuill.Quill.import("attributors/class/font");
  Font.whitelist = ["Manrope"];
  ReactQuill.Quill.register(Font, true);

  // FIX: ofApp only accepts <i> tag for italics, but quill uses <em> by default, this fixes that
  // Source: https://github.com/KillerCodeMonkey/ngx-quill/issues/101#issuecomment-358270459
  const Inline = ReactQuill.Quill.import("blots/inline");
  class ItalicBlot extends Inline {}
  ItalicBlot.blotName = "italic";
  ItalicBlot.tagName = "i";
  ReactQuill.Quill.register(ItalicBlot);

  return (
    <>
      <div className="w-full h-48">
        <ReactQuill
          id={fieldId(name)}
          className="w-full h-36"
          theme="snow"
          onChange={(e) => {
            setWordCount(countWords(e));
            const withoutTags = _.trim(removeTags(e));
            const value = withoutTags.length < 1 ? "" : e;
            const obj = { target: { value } };
            onChange(obj);
          }}
          modules={modules}
          value={getValue}
        />
      </div>
      <div className="text-xs">Words: {wordCount}</div>
    </>
  );
};

const Dropdown = (props: IDropdownProps): JSX.Element => {
  const {
    name,
    options,
    className,
    value,
    onChange,
    modelname,
    required,
    disabled,
  } = props;
  const useOptions =
    options ||
    (modelname &&
      dropdownFields.find((d) => d.model === modelname && d.field === name)
        ?.options) ||
    [];
  const dropdownValue = useOptions?.find((o) => o.value === value) || null;
  return (
    <Select
      name={name}
      options={useOptions}
      value={dropdownValue}
      className={className}
      onChange={(e) => {
        // console.log(e);
        const obj = { target: { value: e?.value } };
        onChange(obj);
      }}
      required={required}
      isDisabled={disabled}
    />
  );
};

const FileUpload = (props: IBaseInputProps): JSX.Element => {
  const { name, onChange, value, accept, required } = props;
  const selectorId = `file_select_${name}`;
  const getFieldId = fieldId(name);

  const [file, setFile] = useState<File | null>(null);
  const [imgUrl, setImgUrl] = useState<string>(_.isString(value) ? value : "");
  const [uploadText, setUploadText] = useState<string>("No file selected");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const hostData = useAtomValue(hostDataAtom);
  const [addToList, removeFromList] = useModifyUploadList(getFieldId);

  // Update upload preview from remote source on element load
  useEffect(() => {
    if (imgUrl !== "") {
      if (isVideo(imgUrl)) {
        getStillVideoFrameFromUrl(imgUrl).then((imageDataUrl) => {
          setUploadText(imgUrl);
          setImgUrl(imageDataUrl);
        });
      } else {
        // const urlParts = imgUrl.split("/");
        setUploadText(imgUrl);
        setImgUrl(imgUrl);
      }
    }
    return () => removeFromList();
  }, []);

  // Update upload preview from local source on `file` change
  useEffect(() => {
    if (file) {
      setFile(file);

      // Set image preview from local source
      if (isVideo(file.name)) {
        const url = URL.createObjectURL(file);
        getStillVideoFrameFromUrl(url).then((imageDataUrl) => {
          setUploadText(`${file.name} - Uploading`);
          setImgUrl(imageDataUrl);
        });
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          setUploadText(file.name);
          if (reader.result) {
            setUploadText(`${file.name} - Uploading`);
            setImgUrl(reader.result.toString());
          }
        };
      }
    }
  }, [file]);

  // const fileSelected = (selectedFile: File | null) => {
  //   if (selectedFile !== null) {
  //     setFile(selectedFile);
  //     startFileUpload(selectedFile); // don't handle on button click anymore
  //   } else {
  //     setFile(null);
  //   }
  // };

  const fileSelected = (selectedFile: File | null) => {
    if (selectedFile !== null) {
      const dateFormatted = format(new Date(), "yyyyMMddHHmmss");
      const newFn = `${dateFormatted}_${selectedFile.name}`;
      const stampedFile = new File([selectedFile], newFn);
      setFile(stampedFile);
      startFileUpload(stampedFile); // don't handle on button click anymore
    } else {
      setFile(null);
    }
  };

  const startFileUpload = async (selectedFile: File | null) => {
    if (selectedFile === null) {
      tMsg.error("Unable to upload selected file");
      return;
    }
    addToList();
    const location = await uploadFile(
      selectedFile,
      onUploadFinished,
      updateProgress,
      hostData.aws_bucket,
      hostData.aws_region
    );
    // console.log("Uploaded file to:", location);
    setUploadText(`Uploading ${selectedFile.name}`);
    setIsUploading(true);
  };

  const onUploadFinished = (fileLocation: string) => {
    setImgUrl(fileLocation);
    const obj = { target: { value: fileLocation } };
    onChange(obj);
    setUploadText(fileLocation);
    setFile(null);
    setIsUploading(false);
    removeFromList();
  };

  const updateProgress = (amount: number) => {
    setUploadText(`Uploading ${file?.name} - ${amount}%`);
  };

  return (
    <div className="w-full flex">
      <input
        type="file"
        onChange={(e) =>
          fileSelected(e.target.files ? e.target.files[0] : null)
        }
        className="w-full"
        accept={accept}
        id={selectorId}
        data-has-url={value !== ""}
        data-file-url={value}
        data-is-direct
        hidden
      />
      <input
        id={getFieldId}
        name={name}
        value={value}
        type="text"
        hidden
        onChange={(e) => e.preventDefault()}
        required={required}
      />
      <div
        id="thumb_container"
        className="w-12 h-auto bg-gray-300 overflow-hidden"
      >
        <img
          src={imgUrl || "/assets/default-image.jpg"}
          className={mergeClass(`object-cover w-full h-full`, {
            "opacity-40": !value || file !== null,
          })}
        />
      </div>
      <div className="flex-grow border border-[#ccc] grid content-center px-2.5 text-sm">
        <div className="flex gap-3">
          <span
            className={mergeClass("break-words", {
              "opacity-50": file !== null || !value,
            })}
          >
            {uploadText}
          </span>
          {isUploading && <Spinner />}
        </div>
      </div>
      <label
        htmlFor={selectorId}
        className={mergeClass(
          "px-5 py-2 grid bg-primaryColor border-primaryColor border text-white font-medium",
          "transition hover:bg-white hover:text-primaryColor hover:border-primaryColor cursor-pointer"
        )}
      >
        {!imgUrl && !file ? "Select file" : "Change file"}
      </label>
    </div>
  );
};

const VideoUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} accept={props.accept || "video/mp4"} />
);
const ImageUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} accept={props.accept || "image/jpeg, image/png"} />
);

const FieldList = {
  number: TextOrNumber,
  string: TextOrNumber,
  string_100: TextOrNumber,
  string_200: TextOrNumber,
  string_400: TextOrNumber,
  bool: Checkbox,
  wysiwyg: RichTextInput,
  file: FileUpload,
  image: ImageUpload,
  video: VideoUpload,
  dropdown: Dropdown,
  string_array: StringArray,
};

const DefaultFieldList = {
  number: null,
  string: "",
  boolean: null,
  wysiwyg: "",
  file: null,
  image: "",
  video: "",
  dropdown: null,
};

const isValidFieldType = (k: string): k is keyof typeof FieldList => {
  return k in FieldList;
};

export { FieldName, FieldList, DefaultFieldList, isValidFieldType, Dropdown };
export type { IBaseInputProps, IDropdownOption };
